import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/tasks/TaskBits";
import { TaskForm } from "@/components/tasks/TaskForm";
import { ActivityRow, TaskDetails, useAccordion } from "@/components/work/Activity";
import api, { apiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const STATUSES = [["in_review", "In review"], ["noted", "Noted"], ["open", "Open"], ["in_progress", "In progress"], ["done", "Done"], ["cancelled", "Cancelled"]];

const viewsFor = (me) => [
  me.can_create && ["mine", "My tasks"],
  me.authority.endorse && ["review", "To review"],
  me.authority.endorse && ["reviewed", "Reviewed"],
  me.authority.receive && ["assigned", "Assigned to me"],
  (me.is_super_admin || me.role === "company_admin") && ["all", "All tasks"],
].filter(Boolean);

const TaskList = ({ tasks }) => {
  const [open, toggle] = useAccordion();
  return (
    <div className="space-y-2.5" data-testid="tasks-list">
      {tasks.length === 0 && <p className="rounded-2xl bg-white px-4 py-10 text-center text-sm text-slate-500 ring-1 ring-slate-200" data-testid="tasks-empty">No tasks here yet</p>}
      {tasks.map((t) => (
        <ActivityRow key={t.id} testid={`task-row-${t.id}`} type={t.last_event?.type || "created"} label={t.last_event?.label || "Created"} title={t.title}
          at={t.last_event?.at || t.updated_at} by={t.last_event?.by || t.created_by.name} pill={<StatusPill t={t} />} statusLabel={t.status_label} to={`/tasks/${t.id}`}
          open={open.has(t.id)} onToggle={() => toggle(t.id)}>
          <TaskDetails t={t} wide />
        </ActivityRow>
      ))}
    </div>
  );
};

export default function TasksPage() {
  const [people, setPeople] = useState(null);
  const [sp, setSp] = useSearchParams();
  const [tasks, setTasks] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const views = people ? viewsFor(people.me) : [];
  const view = sp.get("view") || views[0]?.[0];
  const q = { status: sp.get("status") || "", priority: sp.get("priority") || "", q: sp.get("q") || "", overdue: sp.get("overdue") === "1" };
  const setQ = (p) => setSp((prev) => { const n = new URLSearchParams(prev); Object.entries(p).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k))); return n; }, { replace: true });

  useEffect(() => { api.get("/tasks/people").then((r) => setPeople(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const load = useCallback(() => {
    if (!view) return;
    const params = { view, ...(q.status && { status: q.status }), ...(q.priority && { priority: q.priority }), ...(q.q && { q: q.q }), ...(q.overdue && { overdue: true }) };
    api.get("/tasks", { params }).then((r) => setTasks(r.data.tasks)).catch((e) => toast.error(apiError(e)));
  }, [view, q.status, q.priority, q.q, q.overdue]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.addEventListener("task-updated", load); return () => window.removeEventListener("task-updated", load); }, [load]);

  const create = async (v) => {
    setBusy(true);
    try { await api.post("/tasks", v); toast.success("Task created and sent to the Division Heads"); setCreating(false); setQ({ view: "mine" }); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!people) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  return (
    <div className="space-y-6" data-testid="tasks-page">
      <PageHeader brand eyebrow="Work" title="Tasks" subtitle="Tasks move from the MD to the Division Heads for endorsement, then to the Manager."
        actions={people.me.can_create && <Button onClick={() => setCreating(true)} className="gap-1.5 bg-teal-600 hover:bg-teal-700" data-testid="new-task-button"><Plus className="h-4 w-4" /> New task</Button>} />
      {views.length === 0 ? (
        <p className="rounded-lg border border-slate-200 bg-white px-6 py-10 text-center text-sm text-slate-500" data-testid="tasks-no-authority">You have no task authority yet. Your administrator can grant it.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-white p-1" data-testid="task-views">
            {views.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setQ({ view: k })} className={cn("rounded-md px-3 py-1.5 text-sm font-medium", view === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100")} data-testid={`task-view-${k}`}>{label}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-3" data-testid="task-filters">
            <div className="relative col-span-2 sm:col-span-1"><Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input defaultValue={q.q} placeholder="Search titles" className="h-9 w-full bg-white pl-8 sm:w-56" onKeyDown={(e) => e.key === "Enter" && setQ({ q: e.currentTarget.value })} onBlur={(e) => setQ({ q: e.target.value })} data-testid="task-search-input" /></div>
            <Select value={q.status || "all"} onValueChange={(v) => setQ({ status: v === "all" ? "" : v })}>
              <SelectTrigger className="h-9 w-full bg-white sm:w-40" data-testid="task-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={q.priority || "all"} onValueChange={(v) => setQ({ priority: v === "all" ? "" : v })}>
              <SelectTrigger className="h-9 w-full bg-white sm:w-36" data-testid="task-priority-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All priorities</SelectItem>{["High", "Medium", "Low"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <label className="col-span-2 flex items-center gap-2 text-sm text-slate-600"><Switch checked={q.overdue} onCheckedChange={(v) => setQ({ overdue: v ? "1" : "" })} data-testid="task-overdue-filter" /> Overdue only</label>
          </div>
          {tasks ? <TaskList tasks={tasks} /> : <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
        </>
      )}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-xl" data-testid="new-task-dialog">
          <DialogHeader><DialogTitle>New task</DialogTitle></DialogHeader>
          <TaskForm people={people} busy={busy} onSubmit={create} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
